import React, { useState } from "react";
import { LucideIcon } from "lucide-react";

interface AnimatedButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "success" | "danger";
  size?: "sm" | "md" | "lg";
  icon?: LucideIcon;
  iconPosition?: "left" | "right";
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  type?: "button" | "submit" | "reset";
  "aria-label"?: string;
}

const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  children,
  onClick,
  variant = "primary",
  size = "md",
  icon: Icon,
  iconPosition = "left",
  disabled = false,
  loading = false,
  className = "",
  type = "button",
  "aria-label": ariaLabel,
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const baseClasses =
    "relative inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 transform focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden animate-fade-in hover:scale-105 focus:scale-105";

  const variantClasses = {
    primary:
      "bg-teal-600 hover:bg-teal-700 text-white shadow-md hover:shadow-lg focus:ring-teal-500 dark:bg-teal-500 dark:hover:bg-teal-600",
    secondary:
      "bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-sm hover:shadow-md focus:ring-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200",
    success:
      "bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg focus:ring-green-500 dark:bg-green-500 dark:hover:bg-green-600",
    danger:
      "bg-red-600 hover:bg-red-700 text-white shadow-md hover:shadow-lg focus:ring-red-500 dark:bg-red-500 dark:hover:bg-red-600",
  };

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm gap-1.5",
    md: "px-4 py-2 text-base gap-2",
    lg: "px-6 py-3 text-lg gap-2.5",
  };

  const handleMouseDown = () => setIsPressed(true);
  const handleMouseUp = () => setIsPressed(false);
  const handleMouseLeave = () => setIsPressed(false);

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${isPressed ? "scale-95" : "hover:scale-105"}
        ${loading ? "cursor-wait" : ""}
        ${className}
      `}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      aria-label={ariaLabel}
    >
      {/* Ripple effect */}
      <span className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-200 rounded-lg" />

      {/* Loading spinner */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Content container */}
      <span
        className={`flex items-center gap-inherit ${
          loading ? "opacity-0" : "opacity-100"
        }`}
      >
        {Icon && iconPosition === "left" && <Icon className="w-4 h-4" />}
        {children}
        {Icon && iconPosition === "right" && <Icon className="w-4 h-4" />}
      </span>
    </button>
  );
};

export default AnimatedButton;
